import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AccountLayout } from "./AccountLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { AssetGlyph } from "../Brand";
import api, { formatEUR, formatCrypto, ASSET_ICONS } from "../../lib/api";
import { Download, Search, FileText, Printer } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  awaiting_payment: "bg-amber-50 text-amber-700 border-amber-200",
  sourcing_liquidity: "bg-blue-50 text-blue-700 border-blue-200",
  sending_crypto: "bg-blue-50 text-blue-700 border-blue-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUSES = ["completed", "awaiting_payment", "sourcing_liquidity", "sending_crypto", "failed"];

export default function OrdersHistory() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assetFilter, setAssetFilter] = useState("all");

  useEffect(() => {
    api.get("/v1/orders").then((r) => setOrders(r.data.orders || [])).finally(() => setLoading(false));
  }, []);

  const assets = useMemo(() => Array.from(new Set(orders.map((o) => o.asset))), [orders]);
  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (q && !o.id.toLowerCase().includes(q.toLowerCase()) && !o.asset.toLowerCase().includes(q.toLowerCase())) return false;
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (assetFilter !== "all" && o.asset !== assetFilter) return false;
      return true;
    });
  }, [orders, q, statusFilter, assetFilter]);

  const exportCsv = () => {
    const header = ["order_id", "asset", "network", "fiat_amount_eur", "crypto_amount", "payment_method", "status", "created_at"];
    const rows = filtered.map((o) => [o.id, o.asset, o.network, o.fiat_amount, o.receive_amount, o.payment_method, o.status, o.created_at]
      .map((v) => JSON.stringify(v == null ? "" : v)).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `xchangely-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Exported CSV");
  };

  const downloadTaxCsv = async () => {
    const year = new Date().getFullYear() - 1;
    try {
      const token = localStorage.getItem("xch_token");
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiBase}/api/v1/orders/tax-summary.csv?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) { toast.error(`Failed: ${resp.status}`); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `xchangely_tax_summary_${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Tax summary for ${year} downloaded`);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <AccountLayout
      title="Order history"
      subtitle="All your orders, with status and exports."
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTaxCsv} className="rounded-xl gap-1.5" data-testid="tax-csv-btn">
            <FileText className="w-3.5 h-3.5" /> Tax summary
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={!filtered.length} className="rounded-xl gap-1.5" data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        </div>
      }
    >
      <div className="xch-card p-4 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-testid="orders-filters">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by order ID or asset"
                 className="rounded-xl pl-9" data-testid="orders-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44 rounded-xl" data-testid="orders-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assetFilter} onValueChange={setAssetFilter}>
          <SelectTrigger className="w-full sm:w-32 rounded-xl" data-testid="orders-asset-filter"><SelectValue placeholder="Asset" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assets</SelectItem>
            {assets.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="xch-card overflow-hidden" data-testid="orders-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead><tr className="bg-muted/50 border-b border-border">
              <Th>Order</Th><Th>Asset</Th><Th right>EUR</Th><Th right>Crypto</Th>
              <Th>Method</Th><Th>Status</Th><Th>Date</Th><Th />
            </tr></thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">
                  {orders.length === 0 ? "No orders yet." : "No orders match the filters."}
                </td></tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors" data-testid={`order-${o.id}`}>
                  <td className="px-3 py-3 font-mono text-[11px] text-muted-foreground">{o.id.slice(0, 8)}…</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <AssetGlyph symbol={o.asset} color={ASSET_ICONS[o.asset]} size={24} />
                      <span className="font-display font-bold text-foreground">{o.asset}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums">{formatEUR(o.fiat_amount)}</td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums text-muted-foreground">{formatCrypto(o.receive_amount)}</td>
                  <td className="px-3 py-3 text-xs uppercase font-semibold text-muted-foreground">{o.payment_method}</td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={`${STATUS_TONE[o.status] || "bg-muted text-muted-foreground"} rounded-full font-semibold text-[10px] uppercase tracking-wider border`}>
                      {o.status.replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
                    {new Date(o.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/widget/status/${o.id}`)}
                              className="text-primary hover:bg-primary/5 rounded-lg gap-1 h-7"
                              data-testid={`view-${o.id}`}>
                        <FileText className="w-3.5 h-3.5" /> View
                      </Button>
                      {o.status === "completed" && (
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/account/orders/${o.id}/receipt`)}
                                className="text-muted-foreground hover:text-foreground rounded-lg gap-1 h-7"
                                data-testid={`receipt-${o.id}`}>
                          <Printer className="w-3.5 h-3.5" /> Receipt
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AccountLayout>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);
